/* eslint-disable perfectionist/sort-objects */
import { Role, Roles, PayoutStatus, BackOfficeRoles } from 'types';

import Messages from './messages';

export { Messages };
export { Config } from './env';
export * as Themes from './themes';
export { AppRoutes } from './routes';

export const EmptyPlaceholder = '-';

// .heic/.heif by extension too: Windows Chrome gives them no MIME type.
// DropZone checks their content (utils/heic-to-jpeg heicUploadFile).
export const acceptPdfAndImageFileTypes = {
  'application/pdf': ['.pdf'],
  'image/*': ['.png', '.jpeg', '.jpg', '.heic', '.heif'],
};

export const managerRoles = [
  BackOfficeRoles.ADMIN_REP,
  BackOfficeRoles.MANAGER,
];

export const salesRoles = [...managerRoles, BackOfficeRoles.SALES_REP];

// Owner 26 Oct 2026: who may give an advance (the backend refuses the rest
// with 403).
// - Full: a pay advance (approved at once) or cash handed out at a show;
// - Cash: only cash handed out at a show, from that show's register;
// - None: no advance of either kind.
export enum AdvanceAccess {
  Full = 'full',
  Cash = 'cash',
  None = 'none',
}

// Site Admin is not here: it holds no company and no Event manager feature.
export const advanceFullRoles: Role[] = [
  Roles.COMPANY_OWNER,
  BackOfficeRoles.ADMINISTRATIVE,
];

export const advanceCashOnlyRoles: Role[] = [
  BackOfficeRoles.MANAGER,
  BackOfficeRoles.ADMIN_REP,
];

export const getAdvanceAccess = (role?: string): AdvanceAccess => {
  if (advanceFullRoles.includes(role as Role)) {
    return AdvanceAccess.Full;
  }
  if (advanceCashOnlyRoles.includes(role as Role)) {
    return AdvanceAccess.Cash;
  }
  return AdvanceAccess.None;
};

export const hideCostPriceRoles = [
  BackOfficeRoles.MANAGER,
  BackOfficeRoles.ADMIN_REP,
];

export const approveReimbursementRoles = [
  BackOfficeRoles.ACCOUNTING,
  BackOfficeRoles.ADMINISTRATIVE,
] as Role[];

export const editDecisionStatuses = [
  PayoutStatus.Approved,
  PayoutStatus.NotApproved,
  PayoutStatus.PartialApproved,
];

export const canDeleteReimbursementStatuses = [
  PayoutStatus.Pending,
  PayoutStatus.Approved,
];

export const states = {
  AL: { name: 'Alabama', tax: 4 },
  AK: { name: 'Alaska', tax: 0 },
  AZ: { name: 'Arizona', tax: 5.6 },
  AR: { name: 'Arkansas', tax: 6.5 },
  CA: { name: 'California', tax: 7.25 },
  CO: { name: 'Colorado', tax: 2.9 },
  CT: { name: 'Connecticut', tax: 6.35 },
  DE: { name: 'Delaware', tax: 1.82 },
  FL: { name: 'Florida', tax: 6 },
  GA: { name: 'Georgia', tax: 4 },
  HI: { name: 'Hawaii', tax: 4 },
  ID: { name: 'Idaho', tax: 6 },
  IL: { name: 'Illinois', tax: 6.25 },
  IN: { name: 'Indiana', tax: 7 },
  IA: { name: 'Iowa', tax: 6 },
  KS: { name: 'Kansas', tax: 6.5 },
  KY: { name: 'Kentucky', tax: 6 },
  LA: { name: 'Louisiana', tax: 4.45 },
  ME: { name: 'Maine', tax: 5.5 },
  MD: { name: 'Maryland', tax: 6 },
  MA: { name: 'Massachusetts', tax: 6.25 },
  MI: { name: 'Michigan', tax: 6 },
  MN: { name: 'Minnesota', tax: 6.875 },
  MS: { name: 'Mississippi', tax: 7 },
  MO: { name: 'Missouri', tax: 4.225 },
  MT: { name: 'Montana', tax: 0 },
  NE: { name: 'Nebraska', tax: 5.5 },
  NV: { name: 'Nevada', tax: 6.85 },
  NH: { name: 'New Hampshire', tax: 0 },
  NJ: { name: 'New Jersey', tax: 6.625 },
  NM: { name: 'New Mexico', tax: 5.125 },
  NY: { name: 'New York', tax: 4 },
  NC: { name: 'North Carolina', tax: 4.75 },
  ND: { name: 'North Dakota', tax: 5 },
  OH: { name: 'Ohio', tax: 5.75 },
  OK: { name: 'Oklahoma', tax: 4.5 },
  OR: { name: 'Oregon', tax: 0 },
  PA: { name: 'Pennsylvania', tax: 6 },
  RI: { name: 'Rhode Island', tax: 7 },
  SC: { name: 'South Carolina', tax: 6 },
  SD: { name: 'South Dakota', tax: 4.5 },
  TN: { name: 'Tennessee', tax: 7 },
  TX: { name: 'Texas', tax: 6.25 },
  UT: { name: 'Utah', tax: 4.85 },
  VT: { name: 'Vermont', tax: 6 },
  VA: { name: 'Virginia', tax: 5.3 },
  WA: { name: 'Washington', tax: 6.5 },
  WV: { name: 'West Virginia', tax: 6 },
  WI: { name: 'Wisconsin', tax: 5 },
  WY: { name: 'Wyoming', tax: 4 },
  DC: { name: 'District of Columbia', tax: 6 },
  PR: { name: 'Puerto Rico', tax: 10.5 },
  GU: { name: 'Guam', tax: 4 },
  VI: { name: 'U.S. Virgin Islands', tax: 0 },
  AS: { name: 'American Samoa', tax: 0 },
  MP: { name: 'Northern Mariana Islands', tax: 0 },
};

export const genericCardholderNames = [
  'VISA CARDHOLDER',
  'MASTERCARD CARDHOLDER',
  'MC CARDHOLDER',
  'AMEX CARDMEMBER',
  'CONTACTLESS AMEX',
  'AMERICAN EXPRESS CARDMEMBER',
  'DISCOVER CARDMEMBER',
  'DISCOVER CARDHOLDER',
  'FNB VALUED CUSTOMER',
  'CUSTOMER VALUED',
  'VALUED CUSTOMER',
  'VALUED CARDHOLDER',
  'CARDHOLDER',
  'CARD HOLDER',
  'GIFT CARD HOLDER',
  'PREPAID CARDHOLDER',
  'CORPORATE CARD',
  'BUSINESS CARDHOLDER',
];
